import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import type { BudgetPeriodType, YearlyBudgets as Yearly, YearlyCategoryBudget } from '@/api/budget'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { CategoryIcon } from '@/lib/categoryIcon'
import { formatShortMonth } from '@/lib/formatDate'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { ICON_TILE, PANEL } from './MonthlyBudgets'
import { YearlyBurnChart } from './YearlyBurnChart'

const PERIOD_WORD: Record<BudgetPeriodType, string | null> = {
  MONTHLY: 'month',
  QUARTERLY: 'quarter',
  HALF_YEARLY: 'half',
  YEARLY: null,
}

const FY_MONTHS: Record<string, string> = { '01': 'Jan – Dec', '04': 'Apr – Mar' }

function YearlyRow({
  budget,
  periodType,
  periodMonths,
  open,
  onToggle,
  yearHref,
}: {
  budget: YearlyCategoryBudget
  periodType: BudgetPeriodType
  periodMonths: Set<string>
  open: boolean
  onToggle: () => void
  yearHref: string
}) {
  const { formatWhole } = useFormatCurrency()
  const word = PERIOD_WORD[periodType]
  const left = fromPaise(toPaise(budget.limit) - toPaise(budget.spentToDate))
  // An over-budget bar fills to the total spent, so the limit sits inside it.
  const scale = Math.max(budget.limit, budget.spentToDate) || 1
  const spentMonths = budget.months.filter((m) => m.spent !== 0)

  return (
    <li className="py-3">
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="block w-full rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex items-center gap-2.5 text-sm">
          <CategoryIcon icon={budget.icon} size={13} className={ICON_TILE} />
          <span className="min-w-0 flex-1 truncate font-medium text-[var(--fl-text-1)]">
            {budget.name}
          </span>
          <span
            className={cn(
              'shrink-0 font-semibold tabular-nums',
              budget.isOverBudget ? 'text-[var(--fl-error)]' : 'text-[var(--fl-text-1)]'
            )}
          >
            {formatWhole(budget.spentToDate)}
            <span className="font-normal text-[var(--fl-text-2)]">
              {' '}
              / {formatWhole(budget.limit)}
            </span>
          </span>
        </span>
        <span className="ml-[38px] mt-2 flex h-[7px] overflow-hidden rounded-full bg-[var(--fl-surface-subtle)]">
          <span
            data-testid="year-before"
            className="h-full bg-[var(--fl-hero-bar-faint)]"
            style={{ width: `${(Math.max(budget.spentBefore, 0) / scale) * 100}%` }}
          />
          <span
            data-testid="year-period"
            className={cn(
              'h-full',
              budget.isOverBudget ? 'bg-[var(--fl-error)]' : 'bg-[var(--fl-hero-bar)]'
            )}
            style={{ width: `${(Math.max(budget.spentInPeriod, 0) / scale) * 100}%` }}
          />
        </span>
        <span className="ml-[38px] mt-1.5 flex items-center justify-between gap-2 text-xs text-[var(--fl-text-2)]">
          <span>
            {word == null ? null : budget.spentInPeriod !== 0 ? (
              <span className="rounded-full bg-[var(--fl-hero-bg)] px-2 py-px font-semibold tabular-nums text-[var(--fl-hero-fg)]">
                +{formatWhole(budget.spentInPeriod)} this {word}
              </span>
            ) : (
              <span className="text-[var(--fl-text-3)]">Nothing this {word}</span>
            )}
          </span>
          <span className="flex items-center gap-1 tabular-nums">
            {budget.isOverBudget ? (
              <Badge tone="error">{formatWhole(-left)} over for the year</Badge>
            ) : left === 0 ? (
              'Fully used'
            ) : (
              `${formatWhole(left)} left`
            )}
            <ChevronDown
              size={14}
              aria-hidden="true"
              className={cn('transition-transform', open && 'rotate-180')}
            />
          </span>
        </span>
      </button>

      {open && (
        <div className="mt-2.5 rounded-xl border border-[var(--fl-divider)] bg-[var(--fl-surface-hover)] px-3 py-2.5 md:ml-[38px]">
          <YearlyBurnChart budget={budget} periodMonths={periodMonths} />
          <div className="mt-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <p className="text-xs tabular-nums text-[var(--fl-text-2)]">
              {spentMonths.length === 0
                ? 'Nothing spent this year yet'
                : spentMonths
                    .map((m) => `${formatShortMonth(m.month)} ${formatWhole(m.spent)}`)
                    .join(' · ')}
            </p>
            <Link
              to={yearHref}
              className="text-xs font-semibold text-[var(--fl-primary)] hover:underline"
            >
              This year&rsquo;s transactions →
            </Link>
          </div>
        </div>
      )}
    </li>
  )
}

export function YearlyBudgets({
  yearly,
  periodType,
  periodMonths,
  yearHrefFor,
}: {
  yearly: Yearly
  periodType: BudgetPeriodType
  periodMonths: Set<string>
  yearHrefFor: (categoryId: string) => string
}) {
  const [openId, setOpenId] = useState<string | null>(null)
  const fyMonths = FY_MONTHS[yearly.fyStart.slice(5, 7)]

  return (
    <section aria-label="Yearly budgets" className={PANEL}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 pb-1 pt-3.5">
        <h2 className="text-[15px] font-semibold text-[var(--fl-text-1)]">Yearly budgets</h2>
        <span className="text-xs text-[var(--fl-text-2)]">
          {[yearly.fyLabel, fyMonths, `${yearly.monthsElapsed} of 12 months`]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </div>
      <ul className="divide-y divide-[var(--fl-divider)]">
        {yearly.categories.map((budget) => (
          <YearlyRow
            key={budget.categoryId}
            budget={budget}
            periodType={periodType}
            periodMonths={periodMonths}
            open={openId === budget.categoryId}
            onToggle={() =>
              setOpenId((id) => (id === budget.categoryId ? null : budget.categoryId))
            }
            yearHref={yearHrefFor(budget.categoryId)}
          />
        ))}
      </ul>
    </section>
  )
}
